/**
 * useVideoCallResilience Hook
 * Provides automatic reconnection, connection monitoring, and device failover
 * for AWS Chime SDK video calls
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { MeetingSessionStatusCode } from "amazon-chime-sdk-js";

import {
  getBrowserInfo,
  useReportVideoError,
} from "features/comunications/hooks/useVideoMeetingApi";

import {
  AudioHealthMonitor,
  bindAudioElementWithRetry,
  ConnectionHealthMonitor,
  ConnectionState,
  decideSessionStopAction,
  DeviceManager,
  NetworkQuality,
  retryWithBackoff,
  SessionStopAction,
  sleep,
  VIDEO_CALL_CONFIG,
} from "./videoCallUtils";

const MAX_RECONNECTION_DELAY_MILLISECONDS = 10000;

/**
 * Hook for managing video call resilience
 * @param {Object} options - Configuration options
 * @returns {Object} - Resilience state and handlers
 */
export const useVideoCallResilience = (options = {}) => {
  const {
    onNetworkQualityChange = () => {},
    onAudioRecovered = () => {},
    onDeviceChanged = () => {},
  } = options;

  // Chime observers are registered once per call, so anything they call reads
  // the latest options through this ref rather than the first render's.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const callOption = (name, ...args) => optionsRef.current[name]?.(...args);

  const reportErrorMutation = useReportVideoError();

  // State
  const [connectionState, setConnectionState] = useState(
    ConnectionState.DISCONNECTED
  );
  const [networkQuality, setNetworkQuality] = useState(NetworkQuality.GOOD);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectionAttempts, setReconnectionAttempts] = useState(0);
  const [audioHealthy, setAudioHealthy] = useState(true);

  // Refs
  const meetingSessionRef = useRef(null);
  const meetingInfoRef = useRef({
    meetingId: null,
    attendeeId: null,
    externalUserId: null,
  });
  const connectionMonitorRef = useRef(null);
  const audioMonitorRef = useRef(null);
  const metricsTickCountRef = useRef(0);
  const deviceManagerRef = useRef(null);
  const reconnectionTimeoutRef = useRef(null);
  // Set when the call is torn down and kept until the next call is set up:
  // Chime reports the stop *after* cleanup, and that must not look like a drop.
  const isSessionEndedRef = useRef(false);
  const isReconnectingRef = useRef(false);
  // Bumped per attempt, so a manual retry supersedes one still waiting out
  // its backoff instead of both calling start().
  const reconnectionAttemptIdRef = useRef(0);

  /**
   * Initialize audio health monitor
   */
  const initializeAudioMonitor = useCallback(
    (audioElement) => {
      if (audioMonitorRef.current) {
        audioMonitorRef.current.stop();
      }

      audioMonitorRef.current = new AudioHealthMonitor(audioElement, {
        onSilenceDetected: (info) => {
          setAudioHealthy(false);
          console.warn("Audio silence detected:", info);

          // Only show toast if we had audio before
          if (info.silentChecks === VIDEO_CALL_CONFIG.AUDIO_SILENCE_THRESHOLD) {
            toast.error(
              "No audio from the other side — checking the connection.",
              { id: "audio-silence", duration: 4000 }
            );
            // Report audio issue to backend
            const { meetingId, attendeeId, appointmentId } =
              meetingInfoRef.current;
            if (meetingId) {
              reportErrorMutation.mutate({
                meetingId,
                attendeeId,
                appointmentId,
                errorType: "audio_bind_failed",
                errorMessage: "Audio silence detected after threshold",
                browserInfo: getBrowserInfo(),
                reconnectionAttempts: 0,
              });
            }
          }
        },
        onAudioRecovered: () => {
          setAudioHealthy(true);
          toast.dismiss("audio-silence");
          onAudioRecovered();
        },
        onAudioError: (error) => {
          console.error("Audio error:", error);
          // Report audio error to backend
          const { meetingId, attendeeId, appointmentId } =
            meetingInfoRef.current;
          if (meetingId) {
            reportErrorMutation.mutate({
              meetingId,
              attendeeId,
              appointmentId,
              errorType: "audio_bind_failed",
              errorMessage: error.message || "Audio error",
              browserInfo: getBrowserInfo(),
              reconnectionAttempts: 0,
            });
          }
        },
      });

      audioMonitorRef.current.start();
    },
    [onAudioRecovered, reportErrorMutation]
  );

  /**
   * Initialize device manager
   */
  const initializeDeviceManager = useCallback(
    (meetingSession) => {
      deviceManagerRef.current = new DeviceManager(meetingSession, {
        onDeviceError: (type, error) => {
          console.error(`Device error (${type}):`, error);
          const deviceLabel =
            type === "audioInput"
              ? "microphone"
              : type === "videoInput"
              ? "camera"
              : "speaker";
          toast.error(
            `${deviceLabel} unavailable — switching to a backup device. Check that no other app is using it.`,
            { id: `device-error-${type}`, duration: 4000 }
          );
        },
        onDeviceRecovered: (type, device) => {
          console.log(`Device recovered (${type}):`, device.label);
        },
        onDeviceChanged: (devices) => {
          console.log("Devices changed:", devices);
          onDeviceChanged(devices);
        },
      });

      deviceManagerRef.current.startDeviceChangeListener();
    },
    [onDeviceChanged]
  );

  /**
   * Report error to backend for monitoring
   */
  const reportError = useCallback(
    (errorType, errorMessage) => {
      const { meetingId, attendeeId, appointmentId } = meetingInfoRef.current;
      if (!meetingId) return;

      reportErrorMutation.mutate({
        meetingId,
        attendeeId,
        appointmentId,
        errorType,
        errorMessage,
        browserInfo: getBrowserInfo(),
        reconnectionAttempts:
          connectionMonitorRef.current?.reconnectionAttempts ?? 0,
      });
    },
    [reportErrorMutation]
  );

  /**
   * Restart the microphone the user was on, not whatever is first in the
   * device list, and keep them muted if they were.
   */
  const restartAudioInput = async () => {
    const deviceManager = deviceManagerRef.current;
    if (!deviceManager) return;

    const { deviceId = null, isMuted = false } =
      callOption("getAudioInputState") || {};

    await deviceManager.startAudioInputWithFallback(deviceId);

    if (isMuted) {
      meetingSessionRef.current?.audioVideo.realtimeMuteLocalAudio();
    }
  };
  const restartAudioInputRef = useRef(restartAudioInput);
  restartAudioInputRef.current = restartAudioInput;

  const failReconnection = (message) => {
    isReconnectingRef.current = false;
    setIsReconnecting(false);
    toast.error(message, { id: "reconnecting", duration: 6000 });
    callOption("onReconnectionFailed");
  };

  /**
   * Start the Chime session again after it stopped on a recoverable failure.
   * Success is confirmed by audioVideoDidStart; a failed start comes back
   * through audioVideoDidStop and leads to the next attempt.
   */
  const reconnect = async () => {
    const monitor = connectionMonitorRef.current;
    const meetingSession = meetingSessionRef.current;

    if (isSessionEndedRef.current || isReconnectingRef.current) return;
    if (!monitor || !meetingSession) return;

    if (!monitor.canReconnect()) {
      reportError("connection_lost", "Max reconnection attempts exceeded");
      failReconnection(
        "Couldn't restore the call after several tries. Click 'Leave Call' and rejoin to continue."
      );

      return;
    }

    isReconnectingRef.current = true;
    setIsReconnecting(true);
    reconnectionAttemptIdRef.current += 1;
    const attemptId = reconnectionAttemptIdRef.current;
    const attempt = monitor.incrementReconnectionAttempt();
    setReconnectionAttempts(attempt);
    callOption("onReconnecting", attempt);

    try {
      await sleep(
        Math.min(
          VIDEO_CALL_CONFIG.RECONNECTION_BASE_DELAY * Math.pow(2, attempt - 1),
          MAX_RECONNECTION_DELAY_MILLISECONDS
        )
      );
      const isSuperseded = attemptId !== reconnectionAttemptIdRef.current;
      if (isSessionEndedRef.current || isSuperseded) return;

      // Microphone first, as on the first join: Chime v3 expects an audio
      // input before start().
      await restartAudioInput();
      meetingSession.audioVideo.start();
      callOption("onSessionRestarted");
    } catch (error) {
      console.error("Reconnection attempt failed:", error);
      reportError("connection_lost", error.message);
      isReconnectingRef.current = false;

      if (connectionMonitorRef.current?.canReconnect()) {
        reconnectionTimeoutRef.current = setTimeout(reconnectRef.current, 2000);
      } else {
        failReconnection(
          "Reconnection failed. Click 'Leave Call' and rejoin to continue."
        );
      }
    }
  };
  const reconnectRef = useRef(reconnect);
  reconnectRef.current = reconnect;

  const handleSessionStopped = (sessionStatus) => {
    if (isSessionEndedRef.current) return;

    connectionMonitorRef.current?.setConnectionState(
      ConnectionState.DISCONNECTED
    );

    const statusCode = sessionStatus?.statusCode?.();

    switch (decideSessionStopAction(sessionStatus)) {
      case SessionStopAction.RECONNECT:
        console.warn(
          `Call dropped (${MeetingSessionStatusCode[statusCode]}), reconnecting`
        );
        // A start() that failed lands here too, so clear the attempt in flight.
        isReconnectingRef.current = false;
        reconnectRef.current();
        break;
      case SessionStopAction.JOINED_ELSEWHERE:
        reportError("joined_from_another_device", "AudioJoinedFromAnotherDevice");
        isReconnectingRef.current = false;
        setIsReconnecting(false);
        callOption("onJoinedFromAnotherDevice");
        break;
      case SessionStopAction.GIVE_UP:
        reportError(
          "connection_lost",
          `Call ended by Chime: ${MeetingSessionStatusCode[statusCode]}`
        );
        failReconnection(
          "The call was disconnected. Click 'Leave Call' and rejoin to continue."
        );
        break;
      default:
        break;
    }
  };
  const handleSessionStoppedRef = useRef(handleSessionStopped);
  handleSessionStoppedRef.current = handleSessionStopped;

  const handleSessionStarted = () => {
    connectionMonitorRef.current?.setConnectionState(ConnectionState.CONNECTED);

    if (isReconnectingRef.current) {
      isReconnectingRef.current = false;
      setIsReconnecting(false);
      toast.dismiss("reconnecting");
      callOption("onReconnected");
    }
  };
  const handleSessionStartedRef = useRef(handleSessionStarted);
  handleSessionStartedRef.current = handleSessionStarted;

  /**
   * Initialize connection health monitor
   */
  const initializeConnectionMonitor = useCallback(() => {
    connectionMonitorRef.current = new ConnectionHealthMonitor({
      onConnectionStateChange: (state, prevState) => {
        setConnectionState(state);
        console.log(`Connection state: ${prevState} -> ${state}`);
      },
      onNetworkQualityChange: (quality) => {
        setNetworkQuality(quality);
        onNetworkQualityChange(quality);
      },
      // Sustained packet loss alone doesn't restart anything: Chime recovers
      // a live session itself, and restarting the mic mid-call is what used to
      // drop audio. Only a session Chime has stopped is reconnected.
    });
  }, [onNetworkQualityChange]);

  /**
   * Create Chime observer with resilience handling
   */
  const createResilientObserver = useCallback(
    (customHandlers = {}) => {
      return {
        audioVideoDidStart: async () => {
          console.log("audioVideoDidStart");
          handleSessionStartedRef.current();
          customHandlers.audioVideoDidStart?.();
        },

        audioVideoDidStop: (sessionStatus) => {
          console.log("audioVideoDidStop:", sessionStatus);
          handleSessionStoppedRef.current(sessionStatus);
          customHandlers.audioVideoDidStop?.(sessionStatus);
        },

        audioVideoDidStartConnecting: (reconnecting) => {
          console.log(
            "audioVideoDidStartConnecting, reconnecting:",
            reconnecting
          );
          connectionMonitorRef.current?.setConnectionState(
            reconnecting
              ? ConnectionState.RECONNECTING
              : ConnectionState.CONNECTING
          );
          customHandlers.audioVideoDidStartConnecting?.(reconnecting);
        },

        connectionDidBecomeGood: () => {
          console.log("connectionDidBecomeGood");
          connectionMonitorRef.current?.handleGoodConnection();
          customHandlers.connectionDidBecomeGood?.();
        },

        connectionDidBecomePoor: () => {
          console.warn("connectionDidBecomePoor");
          connectionMonitorRef.current?.handlePoorConnection();
          customHandlers.connectionDidBecomePoor?.();
        },

        connectionDidSuggestStopVideo: () => {
          console.warn("connectionDidSuggestStopVideo - bandwidth is low");
          toast(
            "Video is using too much bandwidth — turn it off if audio cuts out.",
            { id: "low-bandwidth-video", duration: 4000 }
          );
          customHandlers.connectionDidSuggestStopVideo?.();
        },

        videoSendDidBecomeUnavailable: () => {
          console.warn("videoSendDidBecomeUnavailable");
          customHandlers.videoSendDidBecomeUnavailable?.();
        },

        estimatedDownlinkBandwidthLessThanRequired: (
          estimatedDownlinkBandwidthKbps,
          requiredVideoDownlinkBandwidthKbps
        ) => {
          console.warn(
            `Low bandwidth: ${estimatedDownlinkBandwidthKbps}kbps < ${requiredVideoDownlinkBandwidthKbps}kbps required`
          );
          customHandlers.estimatedDownlinkBandwidthLessThanRequired?.(
            estimatedDownlinkBandwidthKbps,
            requiredVideoDownlinkBandwidthKbps
          );
        },

        metricsDidReceive: (clientMetricReport) => {
          // Chime fires this ~1× per second. We don't need that much
          // granularity for the quality banner — sample every 3rd tick
          // (~3s) so a brief Wi-Fi blip doesn't dominate the signal.
          metricsTickCountRef.current = (metricsTickCountRef.current + 1) % 3;
          if (metricsTickCountRef.current !== 0) {
            customHandlers.metricsDidReceive?.(clientMetricReport);
            return;
          }

          // A single bad sample doesn't mean the user's broadband is slow
          // — usually it's a brief Wi-Fi or WebRTC path hiccup. We only
          // flag POOR after several consecutive bad windows (see
          // POOR_CONNECTION_RECONNECT_THRESHOLD) and decay on good ticks.
          const metrics = clientMetricReport?.getObservableMetrics?.();
          if (metrics) {
            const audioPacketLoss = metrics.audioPacketsReceivedFractionLoss;

            if (audioPacketLoss !== undefined) {
              if (
                audioPacketLoss >
                VIDEO_CALL_CONFIG.POOR_CONNECTION_PACKET_LOSS_THRESHOLD
              ) {
                connectionMonitorRef.current?.handlePoorConnection();
              } else {
                connectionMonitorRef.current?.handleGoodMetricTick();
              }
            }
          }
          customHandlers.metricsDidReceive?.(clientMetricReport);
        },

        audioInputStreamEnded: () => {
          console.warn("audioInputStreamEnded - microphone disconnected");
          toast.error(
            "Microphone disconnected — switching to a backup if available.",
            { id: "mic-disconnected", duration: 4000 }
          );

          // Same mic if it comes back, else the next one; mute is kept.
          restartAudioInputRef.current().catch((error) => {
            console.error("Failed to recover audio input:", error);
          });

          customHandlers.audioInputStreamEnded?.();
        },

        videoInputStreamEnded: () => {
          console.warn("videoInputStreamEnded - camera disconnected");
          toast.error("Camera disconnected — your video is off.", {
            id: "camera-disconnected",
            duration: 4000,
          });
          customHandlers.videoInputStreamEnded?.();
        },

        // Video tile observers
        videoTileDidUpdate: customHandlers.videoTileDidUpdate,
        videoTileWasRemoved: customHandlers.videoTileWasRemoved,
      };
    },
    // Everything above is reached through refs, so one observer per call
    // keeps seeing current state.
    []
  );

  /**
   * Setup meeting session with resilience
   * @param {Object} meetingSession - Chime meeting session
   * @param {HTMLAudioElement} audioElement - Audio element for remote audio
   * @param {Object} meetingInfo - Meeting info for backend API calls
   * @param {string} meetingInfo.meetingId - Meeting ID
   * @param {string} meetingInfo.attendeeId - Attendee ID
   * @param {string} meetingInfo.externalUserId - External user ID
   */
  const setupMeetingResilience = useCallback(
    async (meetingSession, audioElement, meetingInfo = {}) => {
      meetingSessionRef.current = meetingSession;
      isSessionEndedRef.current = false;
      isReconnectingRef.current = false;

      // Store meeting info for reconnection API
      meetingInfoRef.current = {
        meetingId: meetingInfo.meetingId || null,
        attendeeId: meetingInfo.attendeeId || null,
        externalUserId: meetingInfo.externalUserId || null,
        appointmentId: meetingInfo.appointmentId || null,
      };

      // Initialize monitors
      initializeConnectionMonitor();
      initializeDeviceManager(meetingSession);

      if (audioElement) {
        initializeAudioMonitor(audioElement);
      }

      connectionMonitorRef.current.setConnectionState(
        ConnectionState.CONNECTING
      );
    },
    [
      initializeConnectionMonitor,
      initializeDeviceManager,
      initializeAudioMonitor,
    ]
  );

  /**
   * Bind audio with retry and monitoring
   */
  const bindAudioWithResilience = useCallback(
    async (meetingSession, audioElement) => {
      try {
        const result = await bindAudioElementWithRetry(
          meetingSession,
          audioElement,
          {
            maxRetries: VIDEO_CALL_CONFIG.MAX_AUDIO_BIND_RETRIES,
            onProgress: (attempt, maxRetries) => {
              console.log(`Binding audio (attempt ${attempt}/${maxRetries})`);
            },
            onSuccess: () => {
              console.log("Audio bound successfully");
              initializeAudioMonitor(audioElement);
            },
            onError: (error) => {
              console.error("Audio binding failed:", error);
              toast.error("Failed to connect audio");
            },
          }
        );

        return result;
      } catch (error) {
        console.error("bindAudioWithResilience error:", error);
        throw error;
      }
    },
    [initializeAudioMonitor]
  );

  /**
   * Initialize devices with fallback
   */
  const initializeDevicesWithFallback = useCallback(
    async (savedPreferences = {}) => {
      if (!deviceManagerRef.current) {
        throw new Error("Device manager not initialized");
      }

      const results = {
        audioInput: null,
        videoInput: null,
        audioOutput: null,
      };

      // Audio input (required)
      try {
        results.audioInput = await retryWithBackoff(
          () =>
            deviceManagerRef.current.startAudioInputWithFallback(
              savedPreferences.audioInput
            ),
          {
            maxRetries: VIDEO_CALL_CONFIG.MAX_DEVICE_INIT_RETRIES,
            baseDelay: VIDEO_CALL_CONFIG.DEVICE_INIT_RETRY_DELAY,
          }
        );
      } catch (error) {
        console.error("Failed to initialize audio input:", error);
        toast.error("Microphone initialization failed");
      }

      // Video input (optional)
      try {
        results.videoInput =
          await deviceManagerRef.current.startVideoInputWithFallback(
            savedPreferences.videoInput
          );
      } catch (error) {
        console.warn("Video initialization failed:", error);
        toast.warning?.("Camera unavailable") || toast("Camera unavailable");
      }

      // Audio output (optional, not on iOS)
      try {
        results.audioOutput =
          await deviceManagerRef.current.setAudioOutputWithFallback(
            savedPreferences.audioOutput
          );
      } catch (error) {
        console.warn("Audio output selection failed:", error);
      }

      return results;
    },
    []
  );

  /**
   * Manual reconnection trigger
   */
  const triggerReconnection = useCallback(() => {
    if (isSessionEndedRef.current || !connectionMonitorRef.current) return;

    clearTimeout(reconnectionTimeoutRef.current);
    connectionMonitorRef.current.reconnectionAttempts = 0;
    isReconnectingRef.current = false;
    reconnectRef.current();
  }, []);

  /**
   * The other attendee left the meeting. Their silence is expected, so it
   * must not raise "No audio from the other side" or an audio report.
   */
  const handleRemoteAttendeeLeft = useCallback(() => {
    audioMonitorRef.current?.resetSilenceTracking();
    setAudioHealthy(true);
    toast.dismiss("audio-silence");
  }, []);

  /**
   * Cleanup all monitors
   */
  const cleanup = useCallback(() => {
    isSessionEndedRef.current = true;
    isReconnectingRef.current = false;
    reconnectionAttemptIdRef.current += 1;

    if (reconnectionTimeoutRef.current) {
      clearTimeout(reconnectionTimeoutRef.current);
    }

    if (audioMonitorRef.current) {
      audioMonitorRef.current.stop();
      audioMonitorRef.current = null;
    }

    if (deviceManagerRef.current) {
      deviceManagerRef.current.stopDeviceChangeListener();
      deviceManagerRef.current = null;
    }

    if (connectionMonitorRef.current) {
      connectionMonitorRef.current.reset();
      connectionMonitorRef.current = null;
    }

    meetingSessionRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    // State
    connectionState,
    networkQuality,
    isReconnecting,
    reconnectionAttempts,
    audioHealthy,

    // Methods
    setupMeetingResilience,
    createResilientObserver,
    bindAudioWithResilience,
    initializeDevicesWithFallback,
    triggerReconnection,
    handleRemoteAttendeeLeft,
    reportError,
    cleanup,

    // Refs (for direct access if needed)
    deviceManagerRef,
    connectionMonitorRef,
    meetingInfoRef,
  };
};

export default useVideoCallResilience;
